/*
 * engraving-form.js
 *
 * Handles the engraving UI on the product page and the custom add-to-cart
 * flow that pairs a "Gravur" service line item with the engraved product.
 *
 * Data bridge: reads all Liquid-generated values from the JSON island
 * rendered by snippets/product-form.liquid:
 *
 *   <script type="application/json" id="engraving-form-data">
 *     { "engravingVariantId": …, "variants": { … }, … }
 *   </script>
 *
 * Single vs. dual-position engraving
 * ------------------------------------
 * When the customer selects one position the behaviour matches the original
 * single-engraving flow (one product item + one "Gravur" service item,
 * flat property names).
 *
 * When both positions are selected, two "Gravur" service items are added —
 * one per position — and the product item carries labeled text properties
 * ("Gravurtext (Klinge)" / "Gravurtext (Griff)") so the position is always
 * identifiable on the product line item.
 */

(() => {
  /* -------------------------------------------------------------------- *
   *  Strings                                                             *
   *  Keep co-located so a future migration to locale files is a simple  *
   *  search-and-replace. (TODO: migrate to `'…' | t` / locales/*.json) *
   * -------------------------------------------------------------------- */
  const STRINGS = {
    placement: {
      blade: {
        id: "placement-blade",
        value: "Gravur auf Klinge",
        label: "Klinge",
      },
      handle: {
        id: "placement-handle",
        value: "Gravur auf Griff",
        label: "Griff",
      },
    },
    placementRequired: "Bitte wähle mindestens eine Position für die Gravur.",
    addToCartFailed: "Could not add to cart: ",
    labelFree: "1. kostenlos",
  };

  /* -------------------------------------------------------------------- *
   *  Server-side config (single JSON island, parsed once)                *
   * -------------------------------------------------------------------- */
  // Guard: the island is always present when the snippet renders the script
  // tag, but a defensive check prevents a crash if the asset is ever
  // requested in a context where the snippet is absent.
  const configEl = document.getElementById("engraving-form-data");
  if (!configEl) return;

  const config = JSON.parse(configEl.textContent);

  /** DOM refs. Populated once by init(). */
  let dom = null;

  // Support both deferred loading (readyState 'interactive') and any edge
  // case where the script might run before parsing is complete.
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  /* -------------------------------------------------------------------- *
   *  Initialisation                                                      *
   * -------------------------------------------------------------------- */
  function init() {
    dom = collectDom();
    if (!dom) return;

    renderPriceInfo();

    // Wire stable elements only. The submit button and hidden id-input live
    // inside `.product-info__add-to-cart` which carries
    // `data-dynamic-variant-content` and is replaced by the variant picker
    // on every variant change — so we must never cache those references.
    dom.engravingToggle.addEventListener("change", handleToggleChange);
    dom.noEngravingToggle.addEventListener("change", handleToggleChange);

    // Attach on `document` (a genuine ancestor of the form), not on the form
    // itself. Capture-phase precedence over the base theme's own
    // <product-form> submit listener (attached directly on the form, see
    // assets/product-form.js) is only guaranteed when we sit on an ancestor:
    // for two listeners on the same target node, the capture flag is ignored
    // and they run in registration order instead — which let the base
    // theme's generic handler win intermittently and add a malformed,
    // unpaired line item straight from the raw form fields. Listening on
    // `document` in the capture phase guarantees this handler runs, and can
    // call stopImmediatePropagation(), before the event ever reaches the
    // form/target.
    document.addEventListener("submit", handleSubmit, true);

    document.addEventListener("on:variant:change", (event) => {
      const variant = event.detail?.variant;
      if (!variant) return;
      render(variant.id);
    });

    render(config.selectedVariantId);
  }

  /* -------------------------------------------------------------------- *
   *  DOM bootstrapping                                                   *
   * -------------------------------------------------------------------- */
  function collectDom() {
    const root = document.getElementById("engraving-root");
    if (!root) return null;

    const productFormEl =
      root.closest("product-form") || document.querySelector("product-form");
    const form = productFormEl?.querySelector('form[action*="/cart/add"]');

    // Sanity-check only — never store the button reference (see comment above).
    const submitButton = form?.querySelector(
      'button[type="submit"][name="add"]',
    );

    if (!form || !submitButton) {
      console.warn("[engraving] host product-form not found; skipping init");
      return null;
    }

    return {
      root,
      form,
      optionContainer: document.getElementById(
        "product-engraving-option-container",
      ),
      fieldsContainer: document.getElementById("product-engraving-container"),
      engravingToggle: document.getElementById("engraving-toggle"),
      noEngravingToggle: document.getElementById("no-engraving-toggle"),
      textsContainer: document.getElementById("engraving-texts-container"),
      placementBtns: document.getElementById("engraving-placement-btns"),
      placementFieldset: document.getElementById(
        "engraving-placement-fieldset",
      ),
      priceLabel: document.getElementById("engraving-note"),
      priceInfo: document.getElementById("engraving-price-info"),
    };
  }

  /**
   * Always re-query the submit button because the variant picker replaces
   * the wrapper element on every variant change.
   */
  function getSubmitButton() {
    return dom.form.querySelector('button[type="submit"][name="add"]');
  }

  /* -------------------------------------------------------------------- *
   *  Pure helpers                                                        *
   * -------------------------------------------------------------------- */
  function getVariantData(variantId) {
    return config.variants[variantId] || null;
  }

  function variantSupportsEngraving(data) {
    return !!data && (data.blade === "Ja" || data.handle === "Ja");
  }

  function getSelectedVariantId() {
    const idInput = dom.form.querySelector('input[name="id"]');
    return idInput ? Number(idInput.value) : config.selectedVariantId;
  }

  function generatePairKey() {
    // base36 timestamp + 6 base36 random chars ≈ 2 billion suffixes per
    // second. Format stays `gravur-…` so existing order queries keep parsing.
    return `gravur-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  }

  /* -------------------------------------------------------------------- *
   *  Rendering                                                           *
   * -------------------------------------------------------------------- */
  function handleToggleChange() {
    render(getSelectedVariantId());
  }

  /**
   * Updates the price shown inside the "Mit Gravur" toggle label.
   * Totals up the cost based on how many positions are selected:
   *   0 or 1 → single engraving price (or "1. kostenlos" when first is free)
   *   2       → double price (or single price when first is free)
   */
  function updatePriceLabel(checkedCount) {
    if (!dom.priceLabel) return;
    if (config.firstEngravingFree) {
      // First engraving is free; second (if chosen) costs the regular price.
      const price = config.engravingVariantPrice
        ? "+ " + config.engravingVariantPrice
        : "";
      dom.priceLabel.textContent =
        checkedCount <= 1 ? STRINGS.labelFree : price;
    } else {
      const raw =
        checkedCount >= 2
          ? config.engravingVariantPrice2x
          : config.engravingVariantPrice;
      dom.priceLabel.textContent = raw ? "+ " + raw : "";
    }
  }

  /**
   * Renders the static info text below the engraving fields.
   * Content is set once via theme settings and never changes dynamically.
   */
  function renderPriceInfo() {
    if (!dom.priceInfo) return;
    const text = config.engravingInfoText || "";
    dom.priceInfo.textContent = text;
    dom.priceInfo.hidden = !text;
  }

  /**
   * Single source of truth for the engraving UI. Shows/hides containers and
   * rebuilds placement buttons + text fields for the given variant.
   */
  function render(variantId) {
    const data = getVariantData(variantId);
    const canEngrave = variantSupportsEngraving(data);
    const wantsEngraving = dom.engravingToggle.checked;

    dom.optionContainer.hidden = !canEngrave;

    const showFields = canEngrave && wantsEngraving;
    dom.fieldsContainer.hidden = !showFields;

    if (showFields) {
      renderPlacementOptions(data);
      clearFields();
      // Preselect the first available placement so the user lands ready to type.
      const firstPlacement = dom.placementBtns.querySelector(
        'input[type="checkbox"]',
      );
      if (firstPlacement) firstPlacement.checked = true;
      renderTextFields();
      setRequired(true);
      updatePriceLabel(firstPlacement ? 1 : 0);
    } else {
      clearFields();
      setRequired(false);
      updatePriceLabel(0);
    }
  }

  function renderPlacementOptions(data) {
    dom.placementBtns.innerHTML = "";
    let hasOptions = false;

    if (data.blade === "Ja") {
      appendPlacementOption(STRINGS.placement.blade);
      hasOptions = true;
    }
    if (data.handle === "Ja") {
      appendPlacementOption(STRINGS.placement.handle);
      hasOptions = true;
    }

    dom.placementFieldset.hidden = !hasOptions;
  }

  /**
   * Rebuild the text-input area to match the currently checked positions:
   *   0 checked  → 1 generic "Gravurtext" field
   *   1 checked  → 1 field labelled "Gravurtext (<Position>)"
   *   2 checked  → 2 fields, one per position
   *
   * Previously typed values are preserved by key so the user does not lose
   * their input when toggling a second position on/off.
   */
  function renderTextFields() {
    const checkedBoxes = Array.from(
      dom.placementBtns.querySelectorAll('input[type="checkbox"]:checked'),
    );

    // Preserve existing values before clearing the container.
    const oldValues = {};
    dom.textsContainer
      .querySelectorAll('input[type="text"]')
      .forEach((input) => {
        oldValues[input.dataset.placementKey] = input.value;
      });

    dom.textsContainer.innerHTML = "";

    const entries =
      checkedBoxes.length === 0
        ? [{ key: "generic", labelText: "Gravurtext" }]
        : checkedBoxes.map((cb) => {
            const labelEl = document.querySelector(`label[for="${cb.id}"]`);
            const posLabel = labelEl ? labelEl.textContent.trim() : cb.value;
            return { key: cb.id, labelText: `Gravurtext (${posLabel})` };
          });

    entries.forEach(({ key, labelText }) => {
      const fieldset = document.createElement("fieldset");
      fieldset.className =
        "option-selector product-engraving__option is-required";

      const legend = document.createElement("legend");
      legend.className = "label";
      legend.setAttribute("title", "Max. " + config.maxTextLength + " Zeichen");
      legend.textContent = labelText;

      const input = document.createElement("input");
      input.type = "text";
      input.className = "input";
      input.maxLength = config.maxTextLength;
      input.placeholder = "Text für Gravur";
      input.dataset.placementKey = key;
      if (oldValues[key]) input.value = oldValues[key];

      fieldset.append(legend, input);
      dom.textsContainer.append(fieldset);
    });
  }

  function appendPlacementOption({ id, value, label }) {
    const input = document.createElement("input");
    input.type = "checkbox";
    input.className = "opt-btn visually-hidden";
    // Bracketed name → native POST would send an array; our JS reads the DOM
    // directly, so this is only for semantic grouping.
    input.name = "properties[Platzierung][]";
    input.id = id;
    input.value = value;
    input.addEventListener("change", () => {
      clearPlacementValidity();
      renderTextFields();
      setRequired(true);
      // Update price info whenever the position count changes.
      const checkedCount = dom.placementBtns.querySelectorAll(
        'input[type="checkbox"]:checked',
      ).length;
      updatePriceLabel(checkedCount);
    });

    const labelEl = document.createElement("label");
    labelEl.htmlFor = id;
    labelEl.className = "opt-label opt-label--btn btn relative text-center";
    labelEl.textContent = label;

    dom.placementBtns.append(input, labelEl);
  }

  /* -------------------------------------------------------------------- *
   *  Field state                                                         *
   * -------------------------------------------------------------------- */
  function clearFields() {
    dom.textsContainer
      .querySelectorAll('input[type="text"]')
      .forEach((input) => {
        input.value = "";
      });

    dom.placementBtns
      .querySelectorAll('input[type="checkbox"]')
      .forEach((cb) => {
        cb.checked = false;
        cb.setCustomValidity("");
      });

    document
      .querySelectorAll('input[name="properties[Schriftart]"]')
      .forEach((radio) => {
        radio.checked = false;
      });
  }

  function setRequired(required) {
    dom.textsContainer
      .querySelectorAll('input[type="text"]')
      .forEach((input) => {
        input.required = required;
      });

    document
      .querySelectorAll('input[name="properties[Schriftart]"]')
      .forEach((radio) => {
        radio.required = required;
      });

    // Placement uses checkboxes (multi-select). Native `required` on a
    // checkbox enforces that *specific* box, defeating the "at least one"
    // requirement. We validate on submit via setCustomValidity instead.
    if (!required) clearPlacementValidity();
  }

  function clearPlacementValidity() {
    dom.placementBtns
      .querySelectorAll('input[type="checkbox"]')
      .forEach((cb) => {
        cb.setCustomValidity("");
      });
  }

  /* -------------------------------------------------------------------- *
   *  Submit handling                                                     *
   * -------------------------------------------------------------------- */
  function readEngravingFields() {
    const allPlacementInputs = Array.from(
      dom.placementBtns.querySelectorAll('input[type="checkbox"]'),
    );
    const checkedPlacements = allPlacementInputs.filter((cb) => cb.checked);
    const fontInput = document.querySelector(
      'input[name="properties[Schriftart]"]:checked',
    );
    const textInputs = Array.from(
      dom.textsContainer.querySelectorAll('input[type="text"]'),
    );

    return {
      checkedPlacements, // checked checkbox elements
      allPlacementInputs, // all placement checkboxes (for validation anchor)
      font: fontInput?.value ?? "",
      textInputs, // text input elements, in placement order
    };
  }

  async function handleSubmit(event) {
    // Now attached on `document`, so this fires for every submit on the
    // page — scope it back down to our own form.
    if (event.target !== dom.form) return;

    // Engraving inactive → let the standard <product-form> ATC flow run.
    if (dom.fieldsContainer.hidden) return;

    // By the time `submit` fires the browser has already validated `required`
    // on the text inputs and font radios. The only remaining validation is
    // "at least one placement selected", since native `required` on a
    // checkbox enforces that specific box instead.
    const fields = readEngravingFields();

    if (fields.checkedPlacements.length === 0) {
      event.preventDefault();
      event.stopImmediatePropagation();

      const anchor = fields.allPlacementInputs[0];
      if (anchor) {
        anchor.setCustomValidity(STRINGS.placementRequired);
        anchor.reportValidity();
      }
      return;
    }

    // Take over from the built-in <product-form> handler. Because this
    // listener now runs during the true capturing phase (registered on
    // `document`, an ancestor of the form), stopping propagation here
    // prevents the event from ever reaching the form's own listeners —
    // including the base theme's <product-form> handler.
    event.preventDefault();
    event.stopImmediatePropagation();

    await addEngravedItemToCart(fields);
  }

  async function addEngravedItemToCart(fields) {
    const submitButton = getSubmitButton();
    submitButton?.setAttribute("aria-busy", "true");
    if (submitButton) submitButton.disabled = true;

    const formData = new FormData(dom.form);
    const productVariantId = Number(formData.get("id"));
    const quantity = parseInt(formData.get("quantity") || "1", 10);
    const pairKey = generatePairKey();

    let productProperties;
    let engravingItems;

    if (fields.checkedPlacements.length === 1) {
      // ── Single position: one engraving service item, flat property names ──
      const text = fields.textInputs[0]?.value.trim() || "";
      const placement = fields.checkedPlacements[0].value;
      const sharedProps = {
        Gravurtext: text,
        Schriftart: fields.font,
        Platzierung: placement,
        "_Gravur-ID": pairKey,
      };
      productProperties = { ...sharedProps };
      engravingItems = [
        {
          id: config.engravingVariantId,
          quantity,
          properties: { Für: config.productTitle, ...sharedProps },
        },
      ];
    } else {
      // ── Two positions: two engraving service items, one per position ──
      // The product item carries labeled text properties so the position is
      // always visible on the product line item in the cart and order.
      const textProps = {};
      const builtEngravings = fields.checkedPlacements.map((cb, index) => {
        const labelEl = document.querySelector(`label[for="${cb.id}"]`);
        const posLabel = labelEl ? labelEl.textContent.trim() : cb.value;
        const text = fields.textInputs[index]?.value.trim() || "";
        textProps[`Gravurtext (${posLabel})`] = text;
        return {
          id: config.engravingVariantId,
          quantity,
          properties: {
            Für: config.productTitle,
            Gravurtext: text,
            Schriftart: fields.font,
            Platzierung: cb.value,
            "_Gravur-ID": pairKey,
          },
        };
      });
      productProperties = {
        ...textProps,
        Schriftart: fields.font,
        "_Gravur-ID": pairKey,
      };
      engravingItems = builtEngravings;
    }

    const items = [
      { id: productVariantId, quantity, properties: productProperties },
      ...engravingItems,
    ];

    try {
      const res = await fetch(`${config.cartAddUrl}.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ items }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || err.message || "Add to cart failed");
      }

      // TODO: dispatch `on:cart:add` here instead of hard-navigating so the
      // theme's cart drawer (if any) opens in place.
      window.location = window.routes?.cart_url || config.cartUrl;
    } catch (err) {
      console.error("[engraving] add to cart failed:", err);
      const errorBox = dom.form.querySelector(".js-form-error");
      if (errorBox) {
        errorBox.hidden = false;
        errorBox.textContent = err.message;
      } else {
        alert(STRINGS.addToCartFailed + err.message);
      }
    } finally {
      // Re-query in case the variant picker swapped the button mid-request.
      const currentBtn = getSubmitButton();
      currentBtn?.removeAttribute("aria-busy");
      if (currentBtn) currentBtn.disabled = false;
    }
  }
})();
